import { isEmpty, isEqual, isNil } from 'lodash';
import { FilterSelectionState } from '@shared/components/filter/types';
import { API } from '@shared/types/api';
import { notNil } from '@shared/util/notNil';

export type RelatedEntityMatch = 'match' | 'noMatch' | 'unknown';

interface GetRelatedEntityMatchProps {
  card: API.WorkflowEntity;
  /**
   * The card as it was when it last passed these filters - set only for a card that's listed. A
   * card that isn't listed may have failed them or may simply be past the loaded page.
   */
  previousCard?: API.WorkflowEntity;
  relatedEntityFilters: FilterSelectionState[];
}

/**
 * Whether a card passes the cross-workflow filters, as far as the client can tell. They match on
 * related cards a subscription event doesn't carry, so `getCardIsInFilterGroup` skips them. Their
 * answer is still known in two cases: a card with no related card at a filter's first hop can't
 * pass a filter that asks for a value on one, and a card that passed still passes while its first
 * hop points at the same related cards, since an edit to any other field leaves the hops' answer as
 * it was. Anything else is `unknown`.
 */
export function getRelatedEntityMatch({
  card,
  previousCard,
  relatedEntityFilters,
}: GetRelatedEntityMatchProps): RelatedEntityMatch {
  const matches = relatedEntityFilters.map((filterOption): RelatedEntityMatch => {
    const componentId = filterOption.filter?.relatedEntityPath?.[0]?.componentId;
    if (isNil(componentId)) {
      return 'unknown';
    }
    const relatedCardIds = getRelatedCardIds(card.fields?.[componentId]);
    if (isEmpty(relatedCardIds) && !canMatchWithoutRelatedCard(filterOption)) {
      return 'noMatch';
    }
    const previousRelatedCardIds = getRelatedCardIds(previousCard?.fields?.[componentId]);
    if (!isNil(previousCard) && isEqual(previousRelatedCardIds, relatedCardIds)) {
      return 'match';
    }
    return 'unknown';
  });
  if (matches.includes('noMatch')) {
    return 'noMatch';
  }
  return matches.includes('unknown') ? 'unknown' : 'match';
}

export function getRelatedEntityFilters(filters: FilterSelectionState[]): FilterSelectionState[] {
  return filters.filter(filterOption => !isEmpty(filterOption.filter?.relatedEntityPath));
}

// Whether a cross-workflow filter might pass a card with no related card at its first hop. core-api
// matches a hop only through a related card, so a card without one fails even an "is empty"
// selection. A negated or yes/no selection stays unknown - how core-api answers those isn't
// mirrored here.
function canMatchWithoutRelatedCard({ selection, negate }: FilterSelectionState): boolean {
  if (negate) {
    return true;
  }
  return !Array.isArray(selection) && 'type' in selection && selection.type === 'BOOLEAN';
}

function getRelatedCardIds(fieldValue: unknown): string[] {
  if (!Array.isArray(fieldValue)) {
    return [];
  }
  return fieldValue
    .map(item => item?.id)
    .filter(notNil)
    .sort();
}
